import { useEffect } from 'react';

export interface KeyboardShortcutHandlers {
  onPlayPause?: () => void;
  onRewind?: () => void;
  onForward?: () => void;
  onFineRewindSec?: () => void;
  onFineForwardSec?: () => void;
  onSpeed1?: () => void;  // 0.5x
  onSpeed2?: () => void;  // 1x
  onSpeed3?: () => void;  // 1.25x
  onSpeed4?: () => void;  // 1.5x
  onPrevUtterance?: () => void;
  onNextUtterance?: () => void;
  onEscape?: () => void;
  onManualSave?: () => void;
  onEntityModal?: () => void;
  onSubmit?: () => void;
  onShowHelp?: () => void;
  onEmotion?: (index: number) => void;
  onSentiment?: (sentiment: 'positive' | 'negative' | 'neutral' | 'mixed') => void;
  onSpeechMarker?: (index: number) => void;
}

export function useKeyboardShortcuts(handlers: KeyboardShortcutHandlers, enabled = true) {
  useEffect(() => {
    if (!enabled) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      const isInputFocused =
        ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName) ||
        target.contentEditable === 'true';

      const isCtrlS = e.ctrlKey && !e.altKey && !e.shiftKey && e.key === 's';
      const isCtrlE = e.ctrlKey && !e.altKey && !e.shiftKey && e.key === 'e';

      // Only Ctrl+S and Ctrl+E are allowed when an input is focused
      if (isInputFocused && !isCtrlS && !isCtrlE) return;

      // ── Ctrl+S — manual save ───────────────────────────────────────────────
      if (isCtrlS) {
        e.preventDefault();
        handlers.onManualSave?.();
        return;
      }

      // ── Ctrl+E — entity modal ──────────────────────────────────────────────
      if (isCtrlE) {
        e.preventDefault();
        handlers.onEntityModal?.();
        return;
      }

      // ── Ctrl+Enter — submit ────────────────────────────────────────────────
      if (e.ctrlKey && !e.altKey && !e.shiftKey && e.key === 'Enter') {
        e.preventDefault();
        handlers.onSubmit?.();
        return;
      }

      // ── Alt+1..9 — emotion shortcuts ──────────────────────────────────────
      if (e.altKey && !e.ctrlKey && !e.shiftKey) {
        const digit = parseInt(e.key, 10);
        if (!isNaN(digit) && digit >= 1 && digit <= 9) {
          e.preventDefault();
          handlers.onEmotion?.(digit - 1);
          return;
        }
      }

      // ── Ctrl+1..8 — speech marker toggles ─────────────────────────────────
      if (e.ctrlKey && !e.altKey && !e.shiftKey) {
        const digit = parseInt(e.key, 10);
        if (!isNaN(digit) && digit >= 1 && digit <= 8) {
          e.preventDefault();
          handlers.onSpeechMarker?.(digit - 1);
          return;
        }
      }

      // All remaining shortcuts are blocked when an input is focused
      if (isInputFocused) return;

      // ── Space — play / pause ───────────────────────────────────────────────
      if (e.key === ' ' && !e.ctrlKey && !e.altKey && !e.shiftKey) {
        e.preventDefault();
        handlers.onPlayPause?.();
        return;
      }

      // ── Arrow keys ─────────────────────────────────────────────────────────
      if (e.key === 'ArrowLeft' && !e.ctrlKey && !e.altKey) {
        e.preventDefault();
        if (e.shiftKey) {
          handlers.onFineRewindSec?.();
        } else {
          handlers.onRewind?.();
        }
        return;
      }

      if (e.key === 'ArrowRight' && !e.ctrlKey && !e.altKey) {
        e.preventDefault();
        if (e.shiftKey) {
          handlers.onFineForwardSec?.();
        } else {
          handlers.onForward?.();
        }
        return;
      }

      // ── ArrowUp / ArrowDown — navigate utterances ──────────────────────────
      if (e.key === 'ArrowUp' && !e.ctrlKey && !e.altKey && !e.shiftKey) {
        e.preventDefault();
        handlers.onPrevUtterance?.();
        return;
      }

      if (e.key === 'ArrowDown' && !e.ctrlKey && !e.altKey && !e.shiftKey) {
        e.preventDefault();
        handlers.onNextUtterance?.();
        return;
      }

      // ── Escape ─────────────────────────────────────────────────────────────
      if (e.key === 'Escape') {
        handlers.onEscape?.();
        return;
      }

      // ── ? — show help ──────────────────────────────────────────────────────
      if (e.key === '?' && !e.ctrlKey && !e.altKey) {
        handlers.onShowHelp?.();
        return;
      }

      // ── 1,2,3,4 — playback speed ───────────────────────────────────────────
      if (!e.ctrlKey && !e.altKey && !e.shiftKey) {
        if (e.key === '1') { handlers.onSpeed1?.(); return; }
        if (e.key === '2') { handlers.onSpeed2?.(); return; }
        if (e.key === '3') { handlers.onSpeed3?.(); return; }
        if (e.key === '4') { handlers.onSpeed4?.(); return; }
      }

      // ── Shift+P/N/U/M — sentiment ──────────────────────────────────────────
      if (e.shiftKey && !e.ctrlKey && !e.altKey) {
        if (e.key === 'P') { handlers.onSentiment?.('positive'); return; }
        if (e.key === 'N') { handlers.onSentiment?.('negative'); return; }
        if (e.key === 'U') { handlers.onSentiment?.('neutral'); return; }
        if (e.key === 'M') { handlers.onSentiment?.('mixed'); return; }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [enabled, handlers]);
}
